// Angular
import {
  ElementRef,
  Injectable,
  Renderer2
} from "@angular/core";

// Third party
import _ from "lodash";
import {
  from,
  concatMap,
  lastValueFrom,
  Observable,
  of,
  Subject,
  toArray
} from "rxjs";
import { WagerFacade, ISelectedRace, ISelectedRaceEloRatings } from "../+state";

// Local library
import {
  abcWagersService,
  betLegRange,
  calcBets,
  dutchWagersService,
  leg,
  LoggerService,
  ticketRequest,
  winDutchService
} from "@amwager/util-ts";
import {
  CommonFacade,
  SessionService
} from "@amwager/data-access";

@Injectable()
export class RunnerService {

  runnerWithReplays: any[] = [];
  indexGetReplays: number = 0;
  replayTypes: number[] = [0, 4, 2, 1, 3];

  /**
   * Cross-component signal that asks any subscribed runner surface
   * (web `runner.component.ts`, future widgets, ...) to reset its
   * wager-selection state — i.e. clear `storeStructure`,
   * `storeStructureCopy`, `nestedArrayABC` and the leg / round DOM
   * highlights — without `BetService` reaching into the component
   * directly. Fired by `BetService.execSaveBet` / `execBetNow` after
   * a confirmed-success API response when the user has the "Clear
   * on Wager" preference enabled.
   *
   * A `Subject` (not `BehaviorSubject`) is intentional: clearing is
   * an event, not a state, so late subscribers shouldn't replay the
   * last clear.
   */
  private readonly _clearRunnerWagerState$ = new Subject<void>();
  readonly clearRunnerWagerState$: Observable<void> =
    this._clearRunnerWagerState$.asObservable();

  constructor(
    private wagerFacade: WagerFacade,
    private sessionService: SessionService
  ) {}

  /**
   * Emit the "reset runner wager state" signal. Subscribers
   * (typically `RunnerComponent`) listen on `clearRunnerWagerState$`
   * and clear their per-bet UI state (storeStructure / copies /
   * nestedArrayABC + leg-round DOM highlights).
   * @returns {void}
   */
  triggerClearRunnerWagerState(): void {
    this._clearRunnerWagerState$.next();
  }

  /**
   * Store cache key for a race's Elo ratings
   * @param {string} eventId Tote event id
   * @param {number} raceId Race id
   * @returns {string} Composite store key
   */
  eloKey(eventId?: string, raceId?: number): string {
    return `${eventId}:${raceId}`;
  }

  /**
   * The join key matching a runner to its Elo entry —
   * `RunnerId` alone, or `RunnerId + EntryId` for coupled entries.
   * @param {number} runnerId Runner id
   * @param {string | number} entryId Coupled-entry id (when present)
   * @returns {string} Full runner id
   */
  getFullRunnerId(runnerId?: number, entryId?: string | number | null): string {
    return entryId !== undefined && entryId !== null && entryId !== ''
      ? `${runnerId}${entryId}`
      : `${runnerId}`;
  }

  /**
   * Dispatch Elo fetches (cache-first) for every race currently shown:
   * the single selected race, or each race of a Pick/DBL pool.
   * @param {ISelectedRace} selectedRace Currently selected race
   * @param {any[]} dblPkArray Races of a multi-race pool (if any)
   * @param {Record<string, ISelectedRaceEloRatings>} cachedRatings Store cache
   * @returns {void}
   */
  refreshEloRatings(
    selectedRace?: ISelectedRace,
    dblPkArray?: any[],
    cachedRatings?: Record<string, ISelectedRaceEloRatings>
  ): void {
    const runId = selectedRace?.runId;
    const eventId = selectedRace?.eventId;
    if (!runId || !eventId) return;

    // Multi-race pools cover several races; single-race pools just one.
    const raceIds: number[] = _.isArray(dblPkArray) && dblPkArray.length
      ? _.uniq(_.map(dblPkArray, (race: any) => race?.RaceId))
      : [selectedRace?.RaceId as number];

    _.forEach(raceIds, (raceId) => {
      if (!raceId) return;

      // Skip refetch when a race is already cached in the store
      if (cachedRatings?.[this.eloKey(eventId, raceId)]?.isSuccess) return;

      this.wagerFacade.getEloRatings('RunnerService:refreshEloRatings', {
        runId,
        eventId,
        raceId
      });
    });
  }

  /**
   * Elo rating to display for a runner in a given race
   * @param {Record<string, ISelectedRaceEloRatings>} cachedRatings Store cache
   * @param {string} eventId Tote event id
   * @param {number} raceId Race the runner belongs to
   * @param {number} runnerId Runner id
   * @param {string | number} entryId Coupled-entry id (when present)
   * @returns {string} Elo rating, or '-' when unavailable
   */
  getRunnerElo(
    cachedRatings?: Record<string, ISelectedRaceEloRatings>,
    eventId?: string,
    raceId?: number,
    runnerId?: number,
    entryId?: string | number | null
  ): string {
    if (!raceId) return '-';

    const fullRunnerId = this.getFullRunnerId(runnerId, entryId);
    const entries = cachedRatings?.[this.eloKey(eventId, raceId)]?.response?.entries;
    const entry = entries?.find((item) => item.entryid === fullRunnerId);

    return entry ? String(entry.aggregaterating) : '-';
  }

  /**
   * Runner stripe
   * @param {any[]} dblPkArray - Double pick array
   * @param {any} runnersInfo - Runners info
   * @param {any} currentEntries - Current entries
   * @param {any} legBets - Leg bets
   * @returns {void}
   */
  runnerStripe(
    dblPkArray: any[] = [], runnersInfo: any, currentEntries: any, legBets: any
  ): void {
    let entriesArray = [];

    if (dblPkArray.length > 0)
      entriesArray = runnersInfo;
    else
      entriesArray = currentEntries;

    _.forEach(entriesArray, (entry: ElementRef, index: number) => {
      const element = entry.nativeElement;

      if (index % 2 === 0)
        element.style.backgroundColor = '#EDF1F5';
      else
        element.style.backgroundColor = '#C1D2E6';
    });

    _.forEach(legBets, (runner: ElementRef, index: number) => {
      const element = runner.nativeElement;

      if (index % 2 === 0)
        element.style.backgroundColor = '#EDF1F5';
      else
        element.style.backgroundColor = '#C1D2E6';
    });
  }

  /**
   * Get replays runner data
   * @param {any} selectedRace - Selected race
   * @param {any[]} dblPkArray - Double pick array
   * @param {any[]} runnersInfo - Runners info
   * @param {any[]} currentEntries - Current entries
   * @returns {Promise<any>} - Runner with empty arrays
   */
  async getReplaysRunnerData(
    selectedRace: any,
    dblPkArray: any[] = [],
    runnersInfo: any[],
    currentEntries: any[]
  ): Promise<any> {

    if (selectedRace.TrackType === undefined) return [];

    const entriesArray = dblPkArray.length > 0 ? runnersInfo : currentEntries;

    let indexLengthToRemove = 0;
    let indexGetReplays = 0;
    const runnerWithReplays: any[] = [];

    const observable$ = from(entriesArray).pipe(
      concatMap((item: any) => {
        indexGetReplays = indexGetReplays + 1;
        const runnerIdEl = item.nativeElement.querySelector('.pos > div > div');
        const runnerNameEl = item.nativeElement.querySelector('.entry-name');
        const scratch = runnerNameEl?.classList.contains('tw-line-through') || false;
        let id = '';

        if (runnerIdEl && runnerNameEl) {
          const rawText = runnerIdEl.innerHTML.trim() || '';
          const numberMatch = rawText.match(/\d+/);
          id = numberMatch ? numberMatch[0] : '';
        }
        else {
          indexLengthToRemove = indexLengthToRemove + 1;
        }

        const name = runnerNameEl ? runnerNameEl.innerHTML.trim() : null;

        if (name && id) {
          const runnerData = {
            runnerId: Number(id),
            name: name,
            availableReplays: [],
            scratch: scratch
          };

          runnerWithReplays.push(runnerData);

          this.wagerFacade.getReplays(
            'RunnerComponent:showReplaysRunnerTable',
            {
              raceDay: '',
              trackType: this.replayTypes[selectedRace.TrackType || 0],
              runnerName: name,
              runnerName2: '',
            }
          );
        }

        return of(null);
      }),
      toArray()
    );

    await lastValueFrom(observable$);

    // indexGetReplays = Math.abs(this.indexGetReplays - indexLengthToRemove);

    return {
      runnerWithReplays,
      indexGetReplays,
      indexLengthToRemove
    };
  }

  /**
   * Set leg bet height based on the number of entries
   * @param {ElementRef} elementRef - Element reference
   * @param {any[]} dblPkArray - Double pick array
   * @param {any[]} runnersInfo - Runners info
   * @param {any[]} currentEntries - Current entries
   * @returns {void} - Number of entries
   */
  setLegBetHeight(
    elementRef: ElementRef,
    dblPkArray: any[] = [],
    runnersInfo: any[],
    currentEntries: any[]
  ): void {
    const entriesArray = dblPkArray.length > 0 ? runnersInfo : currentEntries;

    _.forEach(Array.from(entriesArray), (el, index) => {

      const runnerHeight = el.nativeElement.querySelector('div:first-child').offsetHeight;
      const match = _.find(el.nativeElement.classList, (value) => _.startsWith(value, 'runner-ui-'));
      const num = match?.split('runner-ui-')[1];

      elementRef.nativeElement.querySelector(
        '.leg-ui-' + num + ' > div:first-child'
      ).style.height = runnerHeight + 'px';

      // const lastChilds = elementRef.nativeElement.querySelectorAll(
      //   '.leg-ui-' + num + ' > div:last-child > div:not(:first-child)'
      // );
    });
  }

  /**
   * Set bet leg height pool
   * @param {ElementRef} elementRef - Element reference
   * @param {any[]} runnersInfo - Runners info
   * @param {any[]} legsInfo - Current legs
   * @param {any} selectedPool - Selected pool
   * @param {boolean} addHeight - Add height
   * @returns {void}
   */
  setBetLegHeightPool(
    elementRef: ElementRef,
    runnersInfo: any[],
    legsInfo: any[],
    selectedPool: any,
    addHeight?: boolean
  ): void {

    // OLD (name allowlist skipped multi-race pools like QD/DQ/TT/TS/BQ/TR2/SP2):
    // const startsWithPK = /^PK/.test(selectedPool.PoolId || '');
    // const wordWithPK = /^PICK|Pick|pick/.test(selectedPool.PoolName || '');
    // if (
    //   selectedPool.PoolId === 'GSL' ||
    //   selectedPool.PoolId === 'DBL' ||
    //   startsWithPK || wordWithPK
    // ) {
    if ((selectedPool.PoolRaces || 1) > 1) {

      const poolRaces = selectedPool.PoolRaces;
      const poolLegs = selectedPool.Legs;
      const totalEntries = poolRaces * poolLegs;
      const runnersInfoHeightArr: any[] = [];

      _.forEach(Array.from(runnersInfo), (item, index) => {

        runnersInfoHeightArr.push(item.nativeElement.offsetHeight);
      });

      let i = 0;
      let j = 0;
      let bgColor = '';
      _.forEach(Array.from(legsInfo), (item, index) => {

        if ((i+1) > totalEntries) {
          i = 0;
          j++;
        }

        if (j % 2 === 0)
          bgColor = '#EDF1F5';
        else
          bgColor = '#C1D2E6';

        // item.nativeElement.closest('.bet').classList.add('custom-height');

        item.nativeElement.style.backgroundColor = bgColor;
        item.nativeElement.style.height = runnersInfoHeightArr[j] + 'px';
        i++;
      });

      // const entriesArray = runnersInfo;

      // _.forEach(Array.from(entriesArray), (el, index) => {

      //   const runnerHeight = el.nativeElement.offsetHeight;

      //   const matchUI = _.find(el.nativeElement.classList, (value) =>
      //     _.startsWith(value, 'runner-ui-'));
      //   const matchIds = _.find(el.nativeElement.classList, (value) =>
      //     _.startsWith(value, 'runner-ids-'));
      //   // const match = matchUI || matchIds;
      //   let num = '';
      //   let legId = '';
      //   let legEmpty = '';
      //   let bgColor = '';

      //   if (index % 2 === 0)
      //     bgColor = '#EDF1F5';
      //   else
      //     bgColor = '#C1D2E6';

      //   if (matchIds) {
      //     num = matchIds.split('runner-ids-')[1];
      //     legId = '.leg-id-' + num;
      //   }
      //   else if (matchUI) {
      //     num = matchUI.split('runner-ui-')[1];
      //     legId = '.leg-ui-' + num;
      //     legEmpty = '.leg-id-' + num.split('-')[0];
      //   }

      //   const legs = elementRef.nativeElement.querySelectorAll(
      //     legId
      //   );

      //   _.forEach(legs, (leg) => {
      //     if (leg && matchUI && addHeight)
      //       leg.style.height = runnerHeight + 'px';
      //     else if (leg && matchIds && addHeight)
      //       leg.querySelector('div').style.height = runnerHeight + 'px';

      //     leg.style.backgroundColor = bgColor;
      //   });

      //   if (legEmpty) {
      //     const legsEmpty = elementRef.nativeElement.querySelectorAll(
      //       legEmpty
      //     );
      //     _.forEach(legsEmpty, (leg) => {
      //       if (leg && !leg.querySelector('div').style.height && addHeight)
      //         leg.querySelector('div').style.height = runnerHeight + 'px';

      //       leg.style.backgroundColor = bgColor;
      //     });
      //   }

      // });
    }
  }

  /**
   * To date - Format
   * @param {Date} date - Date
   * @param {number} format - Format
   * @returns {string} - Date
   */
  toDate(date: Date, format: number = 1): string {
    const month = _.padStart(String(date.getMonth()+1), 2, '0');
    const day = _.padStart(String(date.getDate()), 2, '0');

    if(format == 2) return month + "-" + day + "-" + date.getFullYear();
    else return date.getFullYear() + "-" + month + "-" + day;
  }

  /**
   * Bets leg range
   * @param {number} legs - Legs
   * @param {number} poolRaces - Races
   * @param {string} poolId - Pool ID
   * @param {any} currentPool - Current pool
   * @param {string} modifier - Modifier
   * @returns {any[]} - Bets leg range
   */
  betsLegRange(
    legs?: number, poolRaces?: number, poolId?: string, currentPool?: any, modifier?: string
  ): any[] {

    // set default for empty Pools
    if (currentPool === undefined) {
      return [1];
    }

    // OLD (hardcoded per pool name, ignored PoolRaces for anything else):
    // if (poolId === 'GSL') {
    //   legRange = [];
    //   for (let i=1; i<=4; i++) {
    //     legRange.push(i);
    //   }
    //   return legRange;
    // }
    //
    // if (poolId === 'DBL') {
    //   legRange = [];
    //   for (let i=1; i<=2; i++) {
    //     legRange.push(i);
    //   }
    //   return legRange;
    // }
    //
    // const startsWithPK = /^PK/.test(poolId || '');
    // const wordWithPK = /^PICK|Pick|pick/.test(currentPool.PoolName || '');
    // if (startsWithPK || wordWithPK) {
    //   const match = poolId?.match(/(\d+)$/);
    //   if (match) {
    //     legRange = [];
    //     for (let i=1; i<=Number(match[1]); i++) {
    //       legRange.push(i);
    //     }
    //     return legRange;
    //   }
    // }
    //
    // switch (modifier) {
    //   case '':
    //   case 'KB':
    //   case 'PB':
    //   case 'DutchCost':
    //   case 'ABC':
    //     legRange = [];
    //     for (let i=1; i<=(legs||0); i++) {
    //       legRange.push(i);
    //     }
    //   break;
    //
    //   case 'BX':
    //     legRange = [];
    //     legRange.push(1);
    //   break;
    //
    //   case 'KW':
    //     legRange = [];
    //     for (let i=1; i<=2; i++) {
    //       legRange.push(i);
    //     }
    //   break;
    //
    //   default:
    // }
    //
    // return legRange;

    return betLegRange(legs, poolRaces, modifier);
  }

  /**
   * Has permission
   * @param {any} permissionService - Permission service
   * @param {any} router - Router
   * @returns {boolean} - Has permission
   */
  hasPermission(permissionService: any, router: any): boolean {
    if (permissionService.isAllowed()) {
      return true;
    }

    router.navigate(['/login']);
    return false;
  }

  /**
   * Clear custom state
   * @param {any[]} storeStructure - Store structure
   * @param {any[]} storeStructureCopy - Store structure copy
   * @param {ElementRef} elementRef - Element reference
   * @param {any} dblPkArray - Double pk array
   * @param {any} runnersInfo - Runners info
   * @param {any} currentEntries - Current entries
   * @param {any} legBets - Leg
   * @param {any} selectedEvent - Selected event
   * @param {any} selectedRace - Selected race
   * @param {any} selectedPool - Selected pool
   * @param {string} selectedModifier - Selected modifier
   * @param {number} baseAmount - Base amount
   * @param {any[]} poolMatrix - Pool matrix
   * @param {any[]} events - Events
   * @param {number} maxWagerTotal - Max wager total
   * @param {any[]} legResult - Leg result
   * @param {string} dutchROI - Dutch roi
   * @param {any} dutchTicket - Dutch ticket
   * @param {string} dutchAbcErrorMsg - Dutch abc error message
   * @param {any} abcTicket - ABC ticket
   * @param {number} abcTotalAmount - ABC total amount
   * @param {any} countryStateList - Country state list
   * @param {boolean} isBlockJurisdiction - Is block jurisdiction
   * @param {string} isBlockJurisdictionMsg - Is block jurisdiction message
   * @param {boolean} cantBet - Cant bet
   * @returns {any[]} - Clear custom state
   */
  clearCustomState(
    storeStructure: any[],
    storeStructureCopy: any[],
    elementRef: ElementRef,
    dblPkArray: any,
    runnersInfo: any,
    currentEntries: any,
    legBets: any,
    selectedEvent?: any,
    selectedRace?: any,
    selectedPool?: any,
    selectedModifier?: string,
    baseAmount?: number,
    poolMatrix?: any[],
    events?: any[],
    maxWagerTotal?: number,
    legResult?: any[],
    dutchROI?: string,
    dutchTicket?: any,
    dutchAbcErrorMsg?: string,
    abcTicket?: any,
    abcTotalAmount?: number,
    countryStateList?: any,
    isBlockJurisdiction?: boolean,
    isBlockJurisdictionMsg?: string,
    cantBet?: boolean,
  ): {
    resultClearCustomStateStoreStructure: any[],
    resultClearCustomStateStoreStructureCopy: any[],
    resultNestedArrayABC: any,
    showBetResult: any
  } {
    storeStructure = [];
    storeStructureCopy = [];
    const {
      resultNestedArrayABC
    } = this.initNestedArrayABC(
      dblPkArray,
      selectedRace,
      selectedPool,
      selectedModifier || ''
    );
    this.clearLegRoundSelector(elementRef);
    this.clearActiveAllLeg(elementRef);
    const showBetResult = this.showBet(
      false,
      storeStructure,
      dblPkArray,
      runnersInfo,
      currentEntries,
      legBets,
      selectedEvent,
      selectedRace,
      selectedPool,
      selectedModifier,
      baseAmount,
      poolMatrix,
      events,
      maxWagerTotal,
      legResult,
      dutchROI,
      dutchTicket,
      dutchAbcErrorMsg,
      abcTicket,
      abcTotalAmount,
      countryStateList,
      isBlockJurisdiction,
      isBlockJurisdictionMsg,
      cantBet,
      elementRef,
    );

    return {
      resultClearCustomStateStoreStructure: storeStructure,
      resultClearCustomStateStoreStructureCopy: storeStructureCopy,
      resultNestedArrayABC,
      showBetResult: showBetResult?.payload
    }
  }

  /**
   * Clear active all leg
   * @param {ElementRef} elementRef - Element reference
   * @returns {void} - Clear active all leg
   */
  clearActiveAllLeg(elementRef: ElementRef): void {
    const root = elementRef?.nativeElement;
    if (!root) return;

    // `.web-view` is the same flag headLegSelector uses to decide
    // whether the "All" span shows text on web vs. an icon on mobile.
    // We mirror it so we don't accidentally overwrite an icon with the
    // word "All" on the ionic build.
    const webView = root.querySelector('.web-view');

    const divLegs = root.querySelectorAll(`.mode-place-races span.mode`);
    divLegs.forEach((leg: HTMLElement) => {
      leg?.classList.remove('active');
      if (webView) {
        // Restore the default label so a stale "Clear" never bleeds
        // across a successful bet placement, a pool update or a race
        // change.
        leg.innerText = 'All';
      }
    });
  }

  /**
   * Clear leg round selector
   * @param {ElementRef} elementRef - Element reference
   * @returns {void} - Clear leg round selector
   */
  clearLegRoundSelector(elementRef: ElementRef): void {
    const divLegs = elementRef.nativeElement.querySelectorAll(
      '.table-runner i[class^="runner"]'
    );
    divLegs.forEach((leg: HTMLElement) => {
      if (leg?.classList.contains('active') || leg?.classList.contains('couple-entry'))
        leg?.classList.remove('active');
        leg?.classList.remove('couple-entry');
        leg.innerHTML = '';
    });
  }

  /**
   * Convert leg range
   * @param {number} legs - Legs
   * @param {number} poolRaces - Pool races
   * @param {string} poolId - Pool ID
   * @param {any} currentPool - Current pool
   * @param {string} modifier - Modifier
   * @returns {any[]} - Convert leg range
   */
  convertLegRange(
    legs?: number,
    poolRaces?: number,
    poolId?: string,
    currentPool?: any,
    modifier?: string
  ): any[] {

    return this.betsLegRange(
      legs,
      poolRaces,
      poolId,
      currentPool,
      modifier
    );
  }

  /**
   * Group runner entries
   * @param {any[]} data - data
   * @returns {any} - result
   */
  groupRunnerEntries(data: any[]): any {

    const entryGroupsMap: { [key: string]: any[] } = {};

    data.forEach((race: any) => {
      const raceId = race.RaceId;
      race.Runners.forEach((runner: any) => {
        runner.Entries.forEach((entry: any) => {
          const keyParts = [`RunnerId:${runner.RunnerId}`];
          if (entry.EntryId) keyParts.push(`EntryId:${entry.EntryId}`);
          const key = keyParts.join('|');

          if (!entryGroupsMap[key]) {
            entryGroupsMap[key] = [];
          }

          entryGroupsMap[key].push({
            RaceId: raceId,
            RunnerId: runner.RunnerId,
            ...entry,
            Odds: entry?.Odds ?? runner.Odds,
            Scratch: entry?.Scratch ?? runner?.Scratch,
          });
        });
      });
    });

    const result = Object.values(entryGroupsMap).map((entries) => ({
      Entries: entries
    }));

    return result;
  }

  /**
   * Set show current info by race
   * @param {any[]} dblPkArray - Double Pk array
   * @param {any} runnersInfo - Runners info
   * @param {any} currentEntries - Current entries
   * @param {any} legBets - Leg bets
   * @param {ElementRef} elementRef - Element reference
   * @returns {any} - Set show current info by race
   */
  setShowCurrentInfoByRace(
    dblPkArray: any[],
    runnersInfo: any,
    currentEntries: any,
    legBets: any,
    elementRef: ElementRef
  ): {
    resultShowCurrentInfo: number
  } {

    let showCurrentInfo: number = 0;

    if (dblPkArray && dblPkArray.length > 0) {
      showCurrentInfo = dblPkArray[0].RaceId;

      setTimeout(()=>{
        // Race header spans live under `.head-runner`, not under
        // `.mode-place-races .head-bet`. With the old selector the
        // first race indicator was never highlighted after a pool
        // switch, leaving the user without a visual cue.
        const spanLegs = elementRef.nativeElement.querySelectorAll(
          '.waging-section .head-runner #race'
        );
        spanLegs.forEach((leg: HTMLElement, index: number) => {
          leg?.classList.remove('active');
          if (index === 0) {
            leg?.classList.add('active');
          }
        });
      }, 50);

    }

    setTimeout(() => {
      if (runnersInfo && currentEntries && legBets) {
        this.runnerStripe(
          dblPkArray,
          runnersInfo.toArray(),
          currentEntries.toArray(),
          legBets.toArray()
        );
      }
    }, 10);

    return {
      resultShowCurrentInfo: showCurrentInfo
    }
  }

  /**
   * Check pool
   * @param {string} poolId - Pool ID
   * @returns {boolean} - Check pool
   */
  checkPool(poolId: string): boolean {

    let horizontalWager = false;
    switch (poolId) {
      case 'DBL':
      case 'PK3':
      case 'PK4':
      case 'PK5':
      case 'PK6':
      case 'PK7':
      case 'PK8':
      case 'PK9':
      case 'PK10':
      case 'P11':
      case 'PK11':
      case 'P12':
      case 'PK12':
        horizontalWager = true;
      break;
    }

    return horizontalWager;
  }

  /**
   * Init nested array ABC
   * @param {any[]} dblPkArray - Double Pk array
   * @param {any} selectedRace - Selected race
   * @param {any} selectedPool - Selected pool
   * @param {string} selectedModifier - Selected modifier
   * @returns {any} - Init nested array ABC
   */
  initNestedArrayABC(
    dblPkArray: any[],
    selectedRace: any,
    selectedPool: any,
    selectedModifier: string
  ): {
    resultNestedArrayABC: any
  } {
    let nestedArrayABC = [];
    if (selectedModifier.toLowerCase() === 'abc') {
      if (dblPkArray) {

        const {
          resultNestedArrayABC
        } = this.generateNestedArrayPKDbl(
          dblPkArray,
          this.convertLegRange(
            selectedPool?.Legs,
            selectedPool?.PoolRaces,
            selectedPool?.PoolId,
            selectedModifier
          ).length || 0,
          false
        );
        nestedArrayABC = resultNestedArrayABC;

      }
      else {

        const {
          resultNestedArrayABC
        } = this.generateNestedArray(
          selectedRace?.Runners,
          this.convertLegRange(
            selectedPool?.Legs,
            selectedPool?.PoolRaces,
            selectedPool?.PoolId,
            selectedModifier
          ).length || 0,
          false
        );
        nestedArrayABC = resultNestedArrayABC;

      }
    }

    return nestedArrayABC;
  }

  // outerLength: number,
  /**
   * Generate nested array
   * @param {any[]} nestedRunners - Nested runners
   * @param {number} innerLength - Inner length
   * @param {boolean} setCycleA - Set cycle A
   * @returns {any} - Generate nested array
   */
  generateNestedArray(
    nestedRunners: any,
    innerLength: number,
    setCycleA: boolean
  ): {
    resultNestedArrayABC: any
  } {
    const nestedArrayABC = nestedRunners.map((runner: { RunnerId: any; }) =>
      _.times(innerLength, (legIndex) => ({
        legId: legIndex + 1,
        value: setCycleA ? 'A' : '',
        runnerId: runner.RunnerId
      }))
    );

    return {
      resultNestedArrayABC: nestedArrayABC
    }
  }

  /**
   * Generate nested array PK Dbl
   * @param {any[]} races - Races
   * @param {number} legCount - Leg count
   * @param {boolean} setCycleA - Set cycle A
   * @returns {any} - Generate nested array PK Dbl
   */
  generateNestedArrayPKDbl(
    races: any[],
    legCount: number,
    setCycleA: boolean
  ): {
    resultNestedArrayABC: any
  } {
    const nestedArrayABC = races
      .map((race) => {
        return race.Runners?.filter(
          (runner: { toHide: any; }) => !runner.toHide
        ).map((runner: { RunnerId: any; }) => {
          return _.times(legCount, (legIndex) => ({
            legId: race.RaceId,
            value: setCycleA ? 'A' : '',
            runnerId: runner.RunnerId,
          }));
        });
      })
      .filter(Boolean);

    return {
      resultNestedArrayABC: nestedArrayABC
    }
  }

  /**
   * Find ABC value
   * @param {number} legId - Leg ID
   * @param {number} runnerId - Runner ID
   * @param {any[][]} nestedArrayABC - Nested array ABC
   * @returns {string} - Find ABC value
   */
  findABCValue(
    legId: number,
    runnerId: number,
    nestedArrayABC: any[][]
  ): string {

    const allRunners = _.flatMapDeep(nestedArrayABC);

    const legObject: {
      legId: number;
      runnerId: number;
      value: string;
    } | any = _.find(allRunners, { legId, runnerId });

    if (!legObject) {
      return '';
    }

    return legObject.value || '';
  }

  /**
   * Set ABC value
   * @param {number} legId - Leg ID
   * @param {number} runnerId - Runner ID
   * @param {any[][]} nestedArrayABC - Nested array ABC
   * @param {string} newValue - New value
   * @returns {boolean} - Set ABC value
   */
  setABCValue(
    legId: number,
    runnerId: number,
    nestedArrayABC: any[][],
    newValue: string
  ): boolean {

    const allRunners = _.flatMapDeep(nestedArrayABC);

    const legObject: {
      legId: number;
      runnerId: number;
      value: string;
    } | any = _.find(allRunners, { legId, runnerId });

    if (!legObject) {
      return false;
    }

    legObject.value = newValue;
    return true;
  }

  // PoolId: EX, TRI, SPR
  /**
   * Is active runners
   * @param {any[]} nestedArrayABC - Nested array ABC
   * @param {any[]} storeStructure - Store structure
   * @param {string} selectedModifier - Selected modifier
   * @param {ElementRef} elementRef - Element reference
   * @param {Renderer2} renderer - Renderer
   * @returns {void} - Is active runners
   */
  isActiveRunners(
    nestedArrayABC: any[],
    storeStructure: any[],
    selectedModifier: string,
    elementRef: ElementRef,
    renderer: Renderer2
  ): void {
    _.forEach(storeStructure, (item) => {

      let runnersCoupleEntryElementLength = 0;
      const legElement = elementRef.nativeElement.querySelector(`.leg-${item.leg}`);
      const runnersElement = elementRef.nativeElement.querySelectorAll(
        `.table-runner .leg-` + item.leg
      );

      if (legElement) {
        _.forEach(item.runners, (runnerId) => {

          const runnerElement = elementRef.nativeElement.querySelector(
            `.runner-${runnerId}.` + legElement.classList[1]
          );

          const runnersCoupleEntryElement = elementRef.nativeElement.querySelectorAll(
            `.runner-${runnerId}.` + legElement.classList[1] + `.sub-couple-entry`
          );

          if (runnerElement) {
            renderer.addClass(runnerElement, 'active');
            runnerElement.innerHTML = selectedModifier.toLowerCase() === 'abc' ?
            this.findABCValue(Number(item.leg), Number(runnerId), nestedArrayABC)
              :
            runnerId;
          }

          if (runnersCoupleEntryElement.length) {
            runnersCoupleEntryElementLength = runnersCoupleEntryElement.length;
            _.forEach(runnersCoupleEntryElement, (i) => {
              renderer.addClass(i, 'couple-entry');
              // runnerElement.innerHTML = selectedModifier.toLowerCase() === 'abc' ?
              i.innerHTML = selectedModifier.toLowerCase() === 'abc' ?
              this.findABCValue(Number(item.leg), Number(runnerId), nestedArrayABC)
                :
              runnerId + i.getAttribute('data-entry-id');

            });
          }

        });
      }

      if ((runnersElement.length - runnersCoupleEntryElementLength) === item.runners.length) {
        const legAll = elementRef.nativeElement.querySelector(
          `.selector-leg-all-` + item.leg
        );
        renderer.addClass(legAll, 'active');
      }

    });
  }

  /**
   * Get mod ID
   * @param {string} selectedModifier - Selected modifier
   * @returns {any} - Get mod ID
   */
  getModId(selectedModifier: string): any {
    let modId = 0; // default Straight | ABC
    switch (selectedModifier) {
      case 'BX':
        modId = 2;
      break;

      case 'KB':
        modId = 6;
      break;

      case 'PB':
        modId = 3;
      break;

      case 'KW':
        modId = 5;
      break;
    }
    return modId;
  }

  /**
   * Head leg selector
   * @param {any[]} storeStructure - Store structure
   * @param {number} legId - Leg ID
   * @param {ElementRef} elementRef - Element reference
   * @param {Renderer2} renderer - Renderer
   * @returns {void} - Head leg selector
   */
  headLegSelector(
    storeStructure: any[],
    legId: number,
    elementRef: ElementRef,
    renderer: Renderer2
  ): void {
    // header leg
    let z = 0;
    const stored = _.find(storeStructure, ['leg', legId]);

    if (stored === undefined) return;

    const legElement = elementRef.nativeElement.querySelector(`.leg-${legId}`);
    const legAll = elementRef.nativeElement.querySelector(
      `.selector-leg-all-` + legId
    );
    const webView = elementRef.nativeElement.querySelector(`.web-view`);

    if (legElement) {
      const runnerElement = elementRef.nativeElement.querySelectorAll(
        `.table-runner .` + legElement.classList[1]
      );

      const runnerArray = Array.from(runnerElement);

      const uniqueRunners = _.uniqBy(runnerArray, (el: any) => {
        return el.classList[0];
      });

      _.forEach(uniqueRunners, (item) => {
        z++;
      });

      if (z === stored.runners.length && legId === stored.leg) {
        z = 0;
        renderer.addClass(legAll, 'active');
        if (webView)
          renderer.setProperty(legAll, 'innerText', 'Clear');
      }
      else {
        z = 0;
        renderer.removeClass(legAll, 'active');
        if (webView)
          renderer.setProperty(legAll, 'innerText', 'All');
      }
    }
  }

  /**
   * On leg type change
   * @param {any[]} storeStructure - Store structure
   * @param {string} poolId - Pool ID
   * @param {string} prevModifierId - Previous modifier ID
   * @param {ElementRef} elementRef - Element reference
   * @returns {any[]} - On leg type change
   */
  onLegTypeChange(
    storeStructure: any[],
    poolId: string,
    prevModifierId?: string,
    elementRef?: ElementRef
  ): {
    resultOnLegTypeChangeStoreStructure: any[]
  } {
    if (!poolId) return { resultOnLegTypeChangeStoreStructure: storeStructure };

    let newStoreStructure: any;

    switch (poolId) {
      case 'EX':
      case 'QU':
      case 'OMN':
        newStoreStructure = this.updateLegs(storeStructure, 2, prevModifierId, elementRef);
        break;
      case 'TRL':
      case 'TRI':
        newStoreStructure = this.updateLegs(storeStructure, 3, prevModifierId, elementRef);
        break;
      case 'SPR':
        newStoreStructure = this.updateLegs(storeStructure, 4, prevModifierId, elementRef);
        break;
      case 'PEN':
        newStoreStructure = this.updateLegs(storeStructure, 5, prevModifierId, elementRef);
        break;
      case 'HEX':
        newStoreStructure = this.updateLegs(storeStructure, 6, prevModifierId, elementRef);
        break;
      default:
        break;
    }

    const storeStructureCopy = _.cloneDeep(newStoreStructure);

    return {
      resultOnLegTypeChangeStoreStructure: storeStructureCopy
    }
  }

  /**
   * On leg type change modifier
   * @param {any[]} storeStructure - Store structure
   * @param {any[]} storeStructureCopy - Store structure copy
   * @param {string} prevModifierId - Previous modifier ID
   * @param {string} modifierId - Modifier ID
   * @param {string} poolId - Pool ID
   * @param {ElementRef} elementRef - Element reference
   * @returns {any[]} - On leg type change modifier
   */
  onLegTypeChangeModifier(
    storeStructure: any[],
    storeStructureCopy: any[],
    prevModifierId?: string,
    modifierId?: string,
    poolId?: string,
    elementRef?: ElementRef
  ): {
    resultOnLegTypeChangeModifierStoreStructure: any[]
  } {

    this.clearLegRoundSelector(elementRef || new ElementRef(null));
    const newStoreStructureCopy = _.cloneDeep(storeStructureCopy);

    switch (modifierId) {
      case 'BX':
        // 1 leg to show
        storeStructure = _.slice(newStoreStructureCopy, 0, 1);
        break;
      case '':
      case 'KB':
      case 'PB':
        // any legs to show
        storeStructure = newStoreStructureCopy;
        break;
      case 'KW':
        // 2 legs to show
        storeStructure = _.slice(newStoreStructureCopy, 0, 2);
        break;
      default:
        break;
    }

    return {
      resultOnLegTypeChangeModifierStoreStructure: storeStructure
    }
  }

  /**
   * Update legs
   * @param {any[]} storeStructure - Store structure
   * @param {number} requiredLegs - Required legs
   * @param {string} prevModifierId - Previous modifier ID
   * @param {ElementRef} elementRef - Element reference
   * @returns {void} - Update legs
   */
  updateLegs(
    storeStructure: any[],
    requiredLegs: number,
    prevModifierId?: string,
    elementRef?: ElementRef
  ): {
    resultUpdateLegsStoreStructure: any[]
  } {
    const currentLegs = storeStructure.length;
    const legElements = elementRef?.nativeElement.querySelectorAll(
      '.mode-place-races > .head-bet span[class^="mode selector-leg-all-"]'
    );

    if (!legElements || legElements.length === 0)
      return { resultUpdateLegsStoreStructure: storeStructure };

    const lastLeg = _.cloneDeep(_.last(storeStructure));

    if (prevModifierId === 'BX') {
      const {
        resultAddNewLegsStoreStructure
      } = this.addNewLegs(
        storeStructure,
        currentLegs,
        requiredLegs,
        lastLeg
      );
      return { resultUpdateLegsStoreStructure: resultAddNewLegsStoreStructure };
    }

    if (!lastLeg || lastLeg.leg === 1) {
      return { resultUpdateLegsStoreStructure: storeStructure };
    }

    if (currentLegs < requiredLegs) {
      const {
        resultAddNewLegsStoreStructure
      } = this.addNewLegs(
        storeStructure,
        currentLegs,
        requiredLegs,
        lastLeg
      );
      return { resultUpdateLegsStoreStructure: resultAddNewLegsStoreStructure };
    }

    return {
      resultUpdateLegsStoreStructure: storeStructure
    }
  }

  /**
   * Add new legs
   * @param {any[]} storeStructure - Store structure
   * @param {number} currentLegs - Current legs
   * @param {number} requiredLegs - Required legs
   * @param {any} lastLeg - Last leg
   * @returns {any[]} - Add new legs
   */
  addNewLegs(
    storeStructure: any[],
    currentLegs: number,
    requiredLegs: number,
    lastLeg: any
  ): {
    resultAddNewLegsStoreStructure: any[]
  } {
    for (let i = currentLegs; i < requiredLegs; i++) {

      const newLeg = _.cloneDeep(lastLeg);

      if (!newLeg) return { resultAddNewLegsStoreStructure: storeStructure };
        newLeg.leg = i + 1;

      // if does not exist proceed
      if (!_.some(storeStructure, { leg: newLeg.leg })) {
        // add
        storeStructure.push(newLeg);
      }
    }

    return {
      resultAddNewLegsStoreStructure: storeStructure
    }
  }

  /**
   * Re check remove unuse legs
   * @param {any[]} storeStructure - Store structure
   * @param {ElementRef} elementRef - Element reference
   * @param {Renderer2} renderer - Renderer
   * @returns {any[]} - Re check remove unuse legs
   */
  reCheckRemoveUnuseLegs(
    storeStructure: any[],
    elementRef: ElementRef,
    renderer: Renderer2
  ): { resultRemoveUnuseLegsStoreStructure: any[] } {

    const toBeFiltered: number[] = [];
    let hasActiveSelector = false;

    const legElement = elementRef.nativeElement.querySelectorAll(
      '.mode-place-races > .head-bet span[class^="mode selector-leg-all-"]'
    );

    _.forEach(legElement, (item) => {

        const legId = renderer.selectRootElement(item, true).id;
        const runnersElement = elementRef.nativeElement.querySelectorAll(
          `.table-runner .leg-` + legId
        );

        _.forEach(runnersElement, (item) => {
          if (item.classList[2] !== undefined && item.classList[2] === 'active') {
            hasActiveSelector = true;
          }
        });

        if (hasActiveSelector) {
          toBeFiltered.push(Number(legId));
          hasActiveSelector = false;
        }

    });

    const newStoreStructure = _.filter(storeStructure, (leg) => toBeFiltered.includes(leg.leg));

    return {
      resultRemoveUnuseLegsStoreStructure: newStoreStructure
    }
  }

  /**
   * Add leg to store structure
   * @param {number} leg - Leg
   * @param {any[]} storeStructure - Store structure
   * @returns {any[]} - Add leg to store structure
   */
  addLeg(leg: number, storeStructure: any[]): { resultStoreStructure: any[] } {
    storeStructure.push({ leg, runners: [] });
    storeStructure.sort((a, b) => a.leg - b.leg);

    return {
      resultStoreStructure: storeStructure
    }
  }

  /**
   * Add runner to leg
   * @param {number} leg - Leg
   * @param {any} runnerData - Runner data
   * @param {any[]} storeStructure - Store structure
   * @returns {any[]} - Add runner to leg
   */
  addRunnerToLeg(
    leg: number,
    runnerData: any,
    storeStructure: any[]
  ): {
    resultStoreStructure: any[]
  } {
    const legIndex = storeStructure.findIndex(item => item.leg === leg);
    if (legIndex !== -1) {
        const legRunners = storeStructure[legIndex].runners;
        if (!legRunners.includes(runnerData)) {
            legRunners.push(runnerData);
            legRunners.sort((a: number, b: number) => a - b);
        }
    }

    return {
      resultStoreStructure: storeStructure
    }
  }

  /**
   * Remove leg by number
   * @param {number} leg - Leg
   * @param {any[]} storeStructure - Store structure
   * @returns {any[]} - Remove leg by number
   */
  removeLegByNumber(leg: number, storeStructure: any[]): { resultStoreStructure: any[] } {
    const legIndex = storeStructure.findIndex(item => item.leg === leg);
    if (legIndex !== -1) {
      storeStructure.splice(legIndex, 1);
    }

    return {
      resultStoreStructure: storeStructure
    }
  }

  /**
   * Remove runner from leg by value
   * @param {number} leg - Leg
   * @param {any} runnerData - Runner data
   * @param {any[]} storeStructure - Store structure
   * @returns {any[]} - Remove runner from leg by value
   */
  removeRunnerFromLegByValue(
    leg: number,
    runnerData: any,
    storeStructure: any[]
  ): {
    resultStoreStructure: any[]
  } {
    const legIndex = storeStructure.findIndex(item => item.leg === leg);
    if (legIndex !== -1) {
      const legRunners = storeStructure[legIndex].runners;
      const runnerIndex = legRunners.indexOf(runnerData);
      if (runnerIndex !== -1) {
        legRunners.splice(runnerIndex, 1);
      }
    }

    return {
      resultStoreStructure: storeStructure
    }
  }

  /**
   * Format consecutive sequences with legs
   * @param {any[]} data - Data
   * @returns {string} - Formatted consecutive sequences with legs
   */
  formatConsecutiveSequencesWithLegs(data: { leg: number, runners: number[] }[]): string {
    if (data.length === 0) {
      return '';
    }

    let result = '';

    data.forEach((legData, index) => {
      const legResult = this.formatConsecutiveSequences(legData.runners);
      result += legResult;
      if (index < data.length - 1 && legResult !== '') {
        result += '/';
      }
    });
    return result;
  }

  // arr = runners data
  /**
   * Format consecutive sequences
   * @param {number[]} arr - Array of numbers
   * @returns {string} - Formatted consecutive sequences
   */
  formatConsecutiveSequences(arr: number[]): string {
    if (arr.length === 0) {
      return '';
    }

    arr.sort((a, b) => a - b);
    let result = '';
    let consecutiveStart = arr[0];
    let consecutiveEnd = arr[0];

    for (let i = 1; i < arr.length; i++) {
      if (arr[i] === consecutiveEnd + 1) {
        consecutiveEnd = arr[i];
      } else {
        if (consecutiveEnd - consecutiveStart >= 1) {
          if (consecutiveEnd - consecutiveStart >= 2) {
            result += `${consecutiveStart}-${consecutiveEnd},`;
          } else {
            result += `${consecutiveStart},${consecutiveEnd},`;
          }
        } else {
          result += `${consecutiveStart},`;
        }
        consecutiveStart = consecutiveEnd = arr[i];
      }
    }

    if (consecutiveEnd - consecutiveStart >= 1) {
      if (consecutiveEnd - consecutiveStart >= 2) {
        result += `${consecutiveStart}-${consecutiveEnd}`;
      } else {
        result += `${consecutiveStart},${consecutiveEnd}`;
      }
    } else {
      result += `${consecutiveStart}`;
    }

    return result;
  }

  /**
   * Bet runners string format
   * @param {any[]} storeStructure - Store structure
   * @param {string} selectedModifier - Selected modifier
   * @param {ElementRef} elementRef - Element reference
   * @returns {any} - Bet runners string format
   */
  betRunnersStringFormat(
    storeStructure: any[],
    selectedModifier: string,
    elementRef: ElementRef
  ): {
    resultStoreStructure: any[],
    resultStoredSelectedRunners: any
  } {

    // --- OLD CODE (kept for reference) ---
    // Buggy: `storeStructure || []` was evaluated independently in each
    // add* call and the final format step, so when the incoming value was
    // null/undefined every spot allocated its own throwaway array and the
    // re-selected runner produced an empty selection string.
    //
    // let newStoreStructure: any;
    //
    // const divLegs = elementRef.nativeElement.querySelectorAll('.table-runner i[class^="runner"]');
    // divLegs.forEach((leg: HTMLElement) => {
    //   if (leg?.classList.contains('active')) {
    //     if (storeStructure?.length) {
    //       for (let i=0; i < storeStructure.length; i++) {
    //         if (!storeStructure.some(item => item.leg === Number(leg?.id))) {
    //           const {
    //             resultStoreStructure
    //           } = this.addLeg(Number(leg?.id), storeStructure || []);
    //           newStoreStructure = resultStoreStructure;
    //         }
    //       }
    //     }
    //     else {
    //       const {
    //         resultStoreStructure
    //       } = this.addLeg(Number(leg?.id), storeStructure || []);
    //       newStoreStructure = resultStoreStructure;
    //     }
    //   }
    // });
    //
    // divLegs.forEach((leg: HTMLElement, index: number) => {
    //   if (leg?.classList.contains('active')) {
    //     const currentId = selectedModifier.toLowerCase() === 'abc' ?
    //     Number(leg.classList[0].split('-')[1] || 0)
    //       :
    //     leg?.innerHTML.match(/\d+/)?.[0] || '';
    //
    //     const {
    //       resultStoreStructure
    //     } = this.addRunnerToLeg(
    //       Number(leg?.id),
    //       Number(currentId),
    //       storeStructure || []
    //     );
    //     newStoreStructure = resultStoreStructure;
    //   }
    // });
    //
    // const storedSelectedRunners = this.formatConsecutiveSequencesWithLegs(storeStructure || []);
    //
    // return {
    //   resultStoreStructure: newStoreStructure,
    //   resultStoredSelectedRunners: storedSelectedRunners
    // }
    // --- END OLD CODE ---

    // Normalize the incoming structure ONCE and reuse this single
    // reference everywhere below. Previously `storeStructure || []` was
    // evaluated independently in each add* call and the final format
    // step, so when the incoming value was null/undefined (which happens
    // right after all runners are toggled off) every spot allocated its
    // own throwaway array. The leg/runner added in the first pass was
    // then lost before the second pass and the string-format call, so a
    // freshly re-selected runner produced an empty selection string even
    // though it was visibly active in the DOM — surfacing as the
    // "No Runners are selected" bug when rapidly betting/un-betting a leg.
    const workingStructure: any[] = Array.isArray(storeStructure) ? storeStructure : [];

    // Default to the (normalized) working array so callers never receive
    // `undefined` and reset `this.storeStructure` to a non-array value.
    let newStoreStructure: any[] = workingStructure;

    const divLegs = elementRef.nativeElement.querySelectorAll('.table-runner i[class^="runner"]');
    divLegs.forEach((leg: HTMLElement) => {
      if (leg?.classList.contains('active')) {
        if (!workingStructure.some(item => item.leg === Number(leg?.id))) {
          const {
            resultStoreStructure
          } = this.addLeg(Number(leg?.id), workingStructure);
          newStoreStructure = resultStoreStructure;
        }
      }
    });

    divLegs.forEach((leg: HTMLElement) => {
      if (leg?.classList.contains('active')) {
        const currentId = selectedModifier.toLowerCase() === 'abc' ?
        Number(leg.classList[0].split('-')[1] || 0)
          :
        leg?.innerHTML.match(/\d+/)?.[0] || '';

        const {
          resultStoreStructure
        } = this.addRunnerToLeg(
          Number(leg?.id),
          Number(currentId),
          workingStructure
        );
        newStoreStructure = resultStoreStructure;
      }
    });

    const storedSelectedRunners = this.formatConsecutiveSequencesWithLegs(workingStructure);

    return {
      resultStoreStructure: newStoreStructure,
      resultStoredSelectedRunners: storedSelectedRunners
    }
  }

  /**
   * Check exotic
   * @param {string} prevPoolId - Previous pool id
   * @param {string} prevModifierId - Previous modifier id
   * @param {string} modifierId - Modifier id
   * @param {boolean} modifier - Modifier
   * @param {string[]} poolIds - Pool ids
   * @param {boolean} isExoticSelections - Is exotic selections
   * @param {any[]} nestedArrayABC - Nested array abc
   * @param {any} dblPkArray - Double pk array
   * @param {any} legBets - Leg bets
   * @param {any} selectedEvent - Selected event
   * @param {any} selectedRace - Selected race
   * @param {any} selectedPool - Selected pool
   * @param {string} selectedModifier - Selected modifier
   * @param {number} baseAmount - Base amount
   * @param {any[]} poolMatrix - Pool matrix
   * @param {any[]} events - Events
   * @param {number} maxWagerTotal - Max wager total
   * @param {any[]} legResult - Leg result
   * @param {string} dutchROI - Dutch roi
   * @param {any} dutchTicket - Dutch ticket
   * @param {string} dutchAbcErrorMsg - Dutch abc error message
   * @param {any} abcTicket - ABC ticket
   * @param {number} abcTotalAmount - ABC total amount
   * @param {any} countryStateList - Country state list
   * @param {boolean} isBlockJurisdiction - Is block jurisdiction
   * @param {string} isBlockJurisdictionMsg - Is block jurisdiction message
   * @param {any[]} storeStructure - Store structure
   * @param {any[]} storeStructureCopy - Store structure copy
   * @param {any} runnersInfo - Runners info
   * @param {any} currentEntries - Current entries
   * @param {any} currentRunners - Current runners
   * @param {boolean} cantBet - Cant bet
   * @param {ElementRef} elementRef - Element reference
   * @param {any} renderer - Renderer
   * @returns {any} - Check exotic
   */
  checkExotic(
    prevPoolId?: string,
    prevModifierId?: string,
    modifierId?: string,
    modifier?: boolean,
    poolIds?: string[],
    isExoticSelections?: boolean,
    nestedArrayABC?: any[],
    dblPkArray?: any[],
    legBets?: any,
    selectedEvent?: any,
    selectedRace?: any,
    selectedPool?: any,
    selectedModifier?: string,
    baseAmount?: number,
    poolMatrix?: any[],
    events?: any[],
    maxWagerTotal?: number,
    legResult?: any[],
    dutchROI?: string,
    dutchTicket?: any,
    dutchAbcErrorMsg?: string,
    abcTicket?: any,
    abcTotalAmount?: number,
    countryStateList?: any,
    isBlockJurisdiction?: boolean,
    isBlockJurisdictionMsg?: string,
    storeStructure?: any[],
    storeStructureCopy?: any[],
    runnersInfo?: any,
    currentEntries?: any,
    currentRunners?: any,
    cantBet?: boolean,
    elementRef?: ElementRef,
    renderer?: any,
  ): {
    resultIsExotic: boolean,
    resultStoreStructure: any[],
    resultStoredSelectedRunners: string,
    resultShowBetResult: any
  } {

    let newStoreStructure: any;
    let newStoredSelectedRunners: any;
    let showBetResult: any;

    if (
      isExoticSelections &&
      (
        poolIds?.includes(selectedPool?.PoolId)
      ) &&
      (
        poolIds?.includes(prevPoolId || '')
      )
    ) {

      if (currentRunners !== undefined) {
        // setTimeout(()=>{
          if (modifier) {
            const {
              resultOnLegTypeChangeModifierStoreStructure
            } = this.onLegTypeChangeModifier(
              storeStructure || [],
              storeStructureCopy || [],
              prevModifierId,
              modifierId,
              selectedPool?.PoolId,
              elementRef
            );
            newStoreStructure = resultOnLegTypeChangeModifierStoreStructure;
          }
          else {
            const {
              resultOnLegTypeChangeStoreStructure
            } = this.onLegTypeChange(
              storeStructure || [],
              selectedPool?.PoolId,
              prevModifierId,
              elementRef
            );
            newStoreStructure = resultOnLegTypeChangeStoreStructure;
          }

          this.isActiveRunners(
            nestedArrayABC || [],
            storeStructure || [],
            selectedModifier || '',
            elementRef || new ElementRef(null),
            renderer
          );

          const {
            resultRemoveUnuseLegsStoreStructure
          } = this.reCheckRemoveUnuseLegs(
            storeStructure || [],
            elementRef || new ElementRef(null),
            renderer
          );
          newStoreStructure = resultRemoveUnuseLegsStoreStructure;

          const {
            resultStoreStructure,
            resultStoredSelectedRunners
          } = this.betRunnersStringFormat(
            storeStructure || [],
            selectedModifier || '',
            elementRef || new ElementRef(null)
          );
          newStoreStructure = resultStoreStructure;
          newStoredSelectedRunners = resultStoredSelectedRunners;

          showBetResult = this.showBet(
            true,
            storeStructure,
            dblPkArray,
            runnersInfo,
            currentEntries,
            legBets,
            selectedEvent,
            selectedRace,
            selectedPool,
            selectedModifier,
            baseAmount,
            poolMatrix,
            events,
            maxWagerTotal,
            legResult,
            dutchROI,
            dutchTicket,
            dutchAbcErrorMsg,
            abcTicket,
            abcTotalAmount,
            countryStateList,
            isBlockJurisdiction,
            isBlockJurisdictionMsg,
            cantBet,
            elementRef,
          );
        // }, 100);
      }

      return {
        resultIsExotic: true,
        resultStoreStructure: newStoreStructure ?? storeStructure ?? [],
        resultStoredSelectedRunners: newStoredSelectedRunners ?? '',
        resultShowBetResult: showBetResult?.payload
      };
    }
    else return {
      resultIsExotic: false,
      resultStoreStructure: newStoreStructure ?? storeStructure ?? [],
      resultStoredSelectedRunners: newStoredSelectedRunners ?? '',
      resultShowBetResult: showBetResult
    };
  }

  /**
   * Show bet
   * @param {boolean} betTapTriggered - Bet tap triggered
   * @param {any[]} storeStructure - Store structure
   * @param {any[]} dblPkArray - Double pk array
   * @param {any} runnersInfo - Runners info
   * @param {any} currentEntries - Current entries
   * @param {any} legBets - Leg bets
   * @param {any} selectedEvent - Selected event
   * @param {any} selectedRace - Selected race
   * @param {any} selectedPool - Selected pool
   * @param {string} selectedModifier - Selected modifier
   * @param {number} baseAmount - Base amount
   * @param {any[]} poolMatrix - Pool matrix
   * @param {any[]} events - Events
   * @param {number} maxWagerTotal - Max wager total
   * @param {any[]} legResult - Leg result
   * @param {string} dutchROI - Dutch roi
   * @param {any} dutchTicket - Dutch ticket
   * @param {string} dutchAbcErrorMsg - Dutch abc error message
   * @param {any} abcTicket - ABC ticket
   * @param {number} abcTotalAmount - ABC total amount
   * @param {any} countryStateList - Country state list
   * @param {boolean} isBlockJurisdiction - Is block jurisdiction
   * @param {string} isBlockJurisdictionMsg - Is block jurisdiction message
   * @param {boolean} cantBet - Cant bet
   * @param {ElementRef} elementRef - Element reference
   * @param {CommonFacade} commonFacade - Common facade
   * @param {WagerFacade} wagerFacade - Wager facade
   * @returns {void} - Show bet
   */
  showBet(
    betTapTriggered?: boolean,
    storeStructure?: any[],
    dblPkArray?: any[],
    runnersInfo?: any,
    currentEntries?: any,
    legBets?: any,
    selectedEvent?: any,
    selectedRace?: any,
    selectedPool?: any,
    selectedModifier?: string,
    baseAmount?: number,
    poolMatrix?: any[],
    events?: any[],
    maxWagerTotal?: number,
    legResult?: any[],
    dutchROI?: string,
    dutchTicket?: any,
    dutchAbcErrorMsg?: string,
    abcTicket?: any,
    abcTotalAmount?: number,
    countryStateList?: any,
    isBlockJurisdiction?: boolean,
    isBlockJurisdictionMsg?: string,
    cantBet?: boolean,
    elementRef?: ElementRef,
    // commonFacade?: CommonFacade,
    // wagerFacade?: WagerFacade
  ): {
    payload: any
  } {

    // {
    //   resultLegs: any[],
    //   resultBets: number,
    //   resultSelectedRace: any,
    //   resultSelectedPool: any,
    //   resultSelectedModifier: string,
    //   resultDutchTicket: any,
    //   resultDutchROI: string,
    //   resultDutchTotalAmount: number,
    //   resultDutchAbcErrorMsg: string,
    //   resultAbcTicket: any,
    //   resultAbcTotalAmount: number,
    //   resultIsBlockJurisdiction: boolean,
    //   resultIsBlockJurisdictionMsg: string,
    //   resultCantBet: boolean,
    //   resultRunnersWithStake: any,
    //   resultDisableSavedBetBtn: boolean,
    //   resultDisableBetNowBtn: boolean
    // }

    let payload: any = {};
    let legs: any[] = [];
    let betsValue: number = 0;

    // legResult is only kept fresh (via template) for single-race pools;
    // derive it locally for multi-race pools so Save/Submit can enable.
    let effectiveLegResult: any[] = legResult || [];

    let newDutchTotalAmount: number = 0;
    let newDutchROI: string = '';
    let newDutchTicket: any = null;
    let newRunnersWithStake: any = null;
    let newDutchAbcErrorMsg: string = '';
    let newAbcTotalAmount: number = 0;
    let newAbcTicket: any = null;
    let newDisableSavedBetBtn: boolean = false;
    let newDisableBetNowBtn: boolean = false;
    let newIsBlockJurisdiction: boolean = false;
    let newIsBlockJurisdictionMsg: string = '';
    let newCantBet: boolean = false;

    // show bet when user tap selector
    // based on active runners
    // let showBet = false;
    const divLegs = elementRef?.nativeElement.querySelectorAll(
      '.waging-section .table-runner .bet i'
    );

    divLegs?.forEach((leg: HTMLElement) => {
      if (leg?.classList.contains('active')) {
        // showBet = true;
        return;
      }
    });

    // if (showBet && betTapTriggered) {
    //   // betSection.classList.add('active');
    //   commonFacade?.setIsShowFooter(false);
    //   wagerFacade?.setIsShowBetPreview(true);
    // }
    // else {
    //   // betSection.classList.remove('active');
    //   commonFacade?.setIsShowFooter(true);
    //   wagerFacade?.setIsShowBetPreview(false);
    // }

    legs = this.transformRunners(storeStructure || []);

    const runnersEnmList = this.transformLegsRunners(storeStructure || []);

    if (selectedPool !== undefined && runnersEnmList) {
      const bets = calcBets(
        this.convertAbbreToWords(selectedModifier || ''),
        selectedPool.Legs,
        selectedPool.Combine,
        runnersEnmList,
        baseAmount,
        (
          selectedModifier?.toLowerCase() !== 'abc' ||
          selectedModifier?.toLowerCase() !== 'dutchcost' ?
              maxWagerTotal
            :
              selectedPool.Maximum
        ),
        selectedPool.Unordered,
        selectedPool.PoolRaces
      )
      if (bets) {
        betsValue = bets;
      }
      else {
        betsValue = 0;
      }

      // OLD (name allowlist skipped multi-race pools like QD/DQ/TT/TS/BQ/TR2/SP2):
      // const startsWithPK = /^PK/.test(selectedPool.PoolId || '');
      // const wordWithPK = /^PICK|Pick|pick/.test(selectedPool.PoolName || '');
      // if (
      //   selectedPool.PoolId === 'GSL' ||
      //   selectedPool.PoolId === 'DBL' ||
      //   startsWithPK || wordWithPK
      // ) {

      // Multi-race pools render the R1..Rn grid, which never calls
      // betsLegRange from the template, so seed the leg range here.
      if ((selectedPool.PoolRaces || 1) > 1) {
        // OLD (return value discarded, left buttons disabled):
        // this.betsLegRange(
        //   selectedPool.Legs,
        //   selectedPool?.PoolRaces,
        //   selectedPool?.PoolId,
        //   selectedPool,
        //   selectedModifier
        // );

        // Capture the leg range so cantBet checks the correct leg count.
        effectiveLegResult = this.betsLegRange(
          selectedPool.Legs,
          selectedPool?.PoolRaces,
          selectedPool?.PoolId,
          selectedPool,
          selectedModifier
        );
      }
    }

    if (selectedPool) {
      if (
          // OLD: legResult (stale for multi-race) -> effectiveLegResult
          storeStructure?.length === effectiveLegResult?.length &&
          this.betTotal(betsValue, baseAmount || 0) &&
          selectedRace?.RaceStatus !== 1 &&
          selectedRace?.RaceStatus !== 2 &&
          selectedRace?.RaceStatus !== 4 &&
          selectedEvent?.ToteResponse?.EventDetail?.EventStatus !== 'Closed'
        )
        newCantBet = false;
      else
        newCantBet = true;
    }

    // init dutch
    if (dutchROI) {
      dutchTicket = '';
      dutchROI = '';
      dutchAbcErrorMsg = '';
    }

    if (selectedModifier?.toLowerCase() === 'dutchcost') {
      const {
        isBlockJurisdiction: resultIsBlockJurisdiction,
        isBlockJurisdictionMsg: resultIsBlockJurisdictionMsg
      } = this.checkWageringJurisdiction(
        selectedEvent?.ToteResponse?.EventDetail?.EventId,
        countryStateList,
        selectedEvent,
        isBlockJurisdiction || false,
        isBlockJurisdictionMsg || ''
      );

      newIsBlockJurisdiction = resultIsBlockJurisdiction;
      newIsBlockJurisdictionMsg = resultIsBlockJurisdictionMsg;

      const {
        dutchTotalAmount: resultDutchTotalAmount,
        dutchROI: resultDutchROI,
        dutchTicket: resultDutchTicket,
        runnersWithStake: resultRunnersWithStake,
        dutchAbcErrorMsg: resultDutchAbcErrorMsg,
        disableSavedBetBtn: resultDisableSavedBetBtn,
        disableBetNowBtn: resultDisableBetNowBtn,
        cantBet: resultCantBet
      } = this.buildDutchWager(
        storeStructure || [],
        selectedEvent,
        selectedRace,
        selectedModifier,
        selectedPool,
        baseAmount || 0,
        poolMatrix || [],
        events || [],
        maxWagerTotal || 0,
        cantBet || false,
        dutchROI || '',
        dutchTicket,
        dutchAbcErrorMsg || '',
        isBlockJurisdiction || false,
        isBlockJurisdictionMsg || ''
      );

      newDutchTotalAmount = resultDutchTotalAmount;
      newDutchROI = resultDutchROI;
      newDutchTicket = resultDutchTicket;
      newRunnersWithStake = resultRunnersWithStake;
      newDutchAbcErrorMsg = resultDutchAbcErrorMsg;
      newDisableSavedBetBtn = resultDisableSavedBetBtn;
      newDisableBetNowBtn = resultDisableBetNowBtn;
      newCantBet = resultCantBet;
    }

    // init abc
    if (abcTotalAmount)
      abcTotalAmount = 0;

    if (selectedModifier?.toLowerCase() === 'abc') {
      const {
        isBlockJurisdiction: resultIsBlockJurisdiction,
        isBlockJurisdictionMsg: resultIsBlockJurisdictionMsg
      } = this.checkWageringJurisdiction(
        selectedEvent?.ToteResponse?.EventDetail?.EventId,
        countryStateList,
        selectedEvent,
        isBlockJurisdiction || false,
        isBlockJurisdictionMsg || ''
      );

      newIsBlockJurisdiction = resultIsBlockJurisdiction;
      newIsBlockJurisdictionMsg = resultIsBlockJurisdictionMsg;

      const {
        abcTotalAmount: resultAbcTotalAmount,
        abcTicket: resultAbcTicket,
        dutchAbcErrorMsg: resultDutchAbcErrorMsg,
        disableSavedBetBtn: resultDisableSavedBetBtn,
        disableBetNowBtn: resultDisableBetNowBtn,
        cantBet: resultCantBet
      } = this.buildAbcWager(
        storeStructure || [],
        selectedEvent,
        selectedRace,
        selectedModifier,
        selectedPool,
        baseAmount || 0,
        maxWagerTotal || 0,
        cantBet || false,
        abcTicket || [],
        dutchAbcErrorMsg || '',
        isBlockJurisdiction || false,
        isBlockJurisdictionMsg || '',
        elementRef || new ElementRef(null)
      );

      newAbcTotalAmount = resultAbcTotalAmount;
      newAbcTicket = resultAbcTicket;
      newDutchAbcErrorMsg = resultDutchAbcErrorMsg;
      newDisableSavedBetBtn = resultDisableSavedBetBtn;
      newDisableBetNowBtn = resultDisableBetNowBtn;
      newCantBet = resultCantBet;
    }

    setTimeout(() => {
      if (runnersInfo && currentEntries && legBets) {
        this.runnerStripe(
          dblPkArray,
          runnersInfo.toArray(),
          currentEntries.toArray(),
          legBets.toArray()
        );
      }
    });

    payload = {
      legs: legs,
      bets: betsValue,
      selectedRace: selectedRace,
      selectedPool: selectedPool,
      selectedModifier: selectedModifier || '',
      dutchTicket: newDutchTicket,
      dutchROI: newDutchROI,
      dutchTotalAmount: newDutchTotalAmount,
      dutchAbcErrorMsg: newDutchAbcErrorMsg,
      abcTicket: newAbcTicket,
      abcTotalAmount: newAbcTotalAmount,
      baseAmount: baseAmount || 0,
      maxWagerTotal: maxWagerTotal,
      blockJurisdiction: newIsBlockJurisdiction,
      blockJurisdictionMsg: newIsBlockJurisdictionMsg,
      cantBet: newCantBet,
      runnersWithStake: newRunnersWithStake,
      disableSavedBetBtn: newDisableSavedBetBtn,
      disableBetNowBtn: newDisableBetNowBtn
    }

    // payload = {
    //   resultLegs: legs,
    //   resultBets: betsValue,
    //   resultSelectedRace: selectedRace,
    //   resultSelectedPool: selectedPool,
    //   resultSelectedModifier: selectedModifier || '',
    //   resultDutchTicket: newDutchTicket,
    //   resultDutchROI: newDutchROI,
    //   resultDutchTotalAmount: newDutchTotalAmount,
    //   resultDutchAbcErrorMsg: newDutchAbcErrorMsg,
    //   resultAbcTicket: newAbcTicket,
    //   resultAbcTotalAmount: newAbcTotalAmount,
    //   resultIsBlockJurisdiction: newIsBlockJurisdiction,
    //   resultIsBlockJurisdictionMsg: newIsBlockJurisdictionMsg,
    //   resultCantBet: newCantBet,
    //   resultRunnersWithStake: newRunnersWithStake,
    //   resultDisableSavedBetBtn: newDisableSavedBetBtn,
    //   resultDisableBetNowBtn: newDisableBetNowBtn
    // }

    return { payload };
  }

  // Debounce handle for setRunnerChart (coalesces bursts into one paint)
  private doSparklinesTimeout: ReturnType<typeof setTimeout> | null = null;

  // Cache of the last WIN "all odds" series so redraws don't need re-passing
  private lastMatrixWinAll: any[] = [];

  /**
   * Draw a per-runner WIN odds sparkline into each .runner-chart host
   * @param {any[] | null | undefined} data Odds series, or null to reuse cached
   * @param {ElementRef | null} [container] Ref whose subtree holds the placeholders
   * @param {{ PoolRaces?: number; PoolId?: string } | null} [selectedPool] Active pool
   * @returns {void}
   */
  setRunnerChart(
    data: any[] | null | undefined,
    container?: ElementRef | null,
    selectedPool?: { PoolRaces?: number; PoolId?: string } | null,
  ): void {
    if (data != null) {
      this.lastMatrixWinAll = data;
    }

    if (this.doSparklinesTimeout) {
      clearTimeout(this.doSparklinesTimeout);
    }

    this.doSparklinesTimeout = setTimeout(() => {
      this.doSparklinesTimeout = null;

      // Undefined pool = pre-selection default (WIN); only skip
      // multi-race pools where sparklines don't apply.
      if ((selectedPool?.PoolRaces ?? 1) > 1) return;

      const root: HTMLElement | undefined = container?.nativeElement;
      if (!root) return;

      const series = (data != null ? data : this.lastMatrixWinAll) || [];
      if (!series.length) return;

      for (let x = 0; x < series.length; x++) {
        const entry = series[x];
        const points: unknown[] = Array.isArray(entry?.data) ? entry.data : [];
        const start = Math.max(0, points.length - 20);

        const tempData: number[] = [];
        for (let y = start; y < points.length; y++) {
          const raw = (points[y] as [unknown, unknown] | undefined)?.[1];
          if (raw === null || raw === undefined) continue;
          const numeric = Number(raw);
          if (!Number.isFinite(numeric)) continue;
          if (tempData.indexOf(numeric) === -1) tempData.push(numeric);
        }

        if (tempData.length < 2) continue;

        const runnerId = this.extractRunnerIdFromSeries(entry, x);
        const host = root.querySelector<HTMLElement>(
          `.runner-chart[data-runner-id="${runnerId}"]`
        );
        if (!host) continue;

        const oddsText = this.readOddsCellText(host);
        if (oddsText === '-' || oddsText === '--' || oddsText === 'SCR') continue;

        const svg = this.renderSparklineSvg(tempData);
        const existing = host.querySelector('svg.sparkline');
        if (existing) existing.replaceWith(svg);
        else host.appendChild(svg);
      }
    }, 500);
  }

  /**
   * Recover the runner id (ColumnNumber) from a series entry
   * @param {any} entry Series entry
   * @param {number} index Loop index fallback
   * @returns {number} Recovered runner id (1-indexed)
   */
  private extractRunnerIdFromSeries(entry: any, index: number): number {
    const label = typeof entry?.label === 'string' ? entry.label.replace(/^#/, '') : '';
    const fromLabel = Number.parseInt(label, 10);
    if (Number.isFinite(fromLabel) && fromLabel > 0) return fromLabel;

    const fromColor = Number(entry?.color);
    if (Number.isFinite(fromColor)) return fromColor + 1;

    return index + 1;
  }

  /**
   * Read the visible WIN-odds text for the row that owns chartHost
   * @param {HTMLElement} chartHost The .runner-chart element
   * @returns {string | null} Trimmed odds text, or null when empty
   */
  private readOddsCellText(chartHost: HTMLElement): string | null {
    const row = chartHost.closest('.runner-group');
    const oddsCell = row?.querySelector('.odds');
    const text = oddsCell?.textContent?.trim();
    return text || null;
  }

  /**
   * Build an SVG sparkline from the given odds series
   * @param {number[]} values Series values (at least 2 required)
   * @returns {SVGSVGElement} Newly-created SVG element
   */
  private renderSparklineSvg(values: number[]): SVGSVGElement {
    const width = 60;
    const height = 20;
    const padX = 2;
    const padY = 2;
    const svgNS = 'http://www.w3.org/2000/svg';

    const svg = document.createElementNS(svgNS, 'svg') as SVGSVGElement;
    svg.setAttribute('class', 'sparkline sparkline--red');
    svg.setAttribute('width', String(width));
    svg.setAttribute('height', String(height));
    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    svg.setAttribute('preserveAspectRatio', 'none');

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const stepX = (width - 2 * padX) / Math.max(1, values.length - 1);

    let d = '';
    for (let i = 0; i < values.length; i++) {
      const x = padX + i * stepX;
      const y = height - padY - ((values[i] - min) / range) * (height - 2 * padY);
      d += (i === 0 ? 'M' : ' L') + x.toFixed(2) + ',' + y.toFixed(2);
    }

    const path = document.createElementNS(svgNS, 'path');
    path.setAttribute('d', d);
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', '#dc3545');
    path.setAttribute('stroke-width', '2');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('vector-effect', 'non-scaling-stroke');
    svg.appendChild(path);

    return svg;
  }

  /**
   * Map raw runners to { Auto, Runners } bet-leg entries
   * @param {any[]} data Raw runner rows with a `runners` array
   * @returns {any[]} Leg entries
   */
  transformRunners(data: any[]): any[] {
    return _.map(data, item => {
      return {
        Auto: false,
        Runners: item.runners.join(',')
      };
    });
  }

  /**
   * Convert leg runner arrays to string[][] for the API payload
   * @param {any[]} data Legs with numeric runner arrays
   * @returns {any[]} Legs with stringified runners
   */
  transformLegsRunners(data: any[]): any[] {
    return _.times(
      data.length, i => _.map(
        data[i].runners, String
      )
    );
  }

  /**
   * Expand modifier abbreviation into full name (BX -> Box, KB -> KeyBox...)
   * @param {string} abbreviation Modifier abbreviation
   * @returns {string} Full modifier name, or 'Straight' as default
   */
  convertAbbreToWords(abbreviation: string): string {
    let modifier =  abbreviation;

    switch(modifier) {
      case 'BX':
          modifier = 'Box'
        break;
      case 'KB':
          modifier = 'KeyBox'
        break;
      case 'PB':
          modifier = 'PowerBox'
        break;
      case 'KW':
          modifier = 'KeyWheel'
        break;
      default:
          modifier = 'Straight'
        break;
    }

    return modifier;
  }

  /**
   * Compute total bet amount from bets count and base amount
   * @param {number} bets Number of bets
   * @param {number} baseAmount Base amount per bet
   * @returns {number} Total bet amount
   */
  betTotal(bets: number, baseAmount: number): number {
    return ((bets * 100) * (baseAmount * 100)) / 10000;
  }

  /**
   * Evaluate whether the user's jurisdiction blocks this event
   * @param {any} eventId Event id being checked
   * @param {any} countryStateList Country/state jurisdiction lookup
   * @param {any} selectedEvent Selected event
   * @param {boolean} isBlockJurisdiction Current block flag
   * @param {string} isBlockJurisdictionMsg Current block message
   * @returns {{isBlockJurisdiction: boolean, isBlockJurisdictionMsg: string}} Resolved block state
   */
  checkWageringJurisdiction(
    eventId: any,
    countryStateList: any,
    selectedEvent: any,
    isBlockJurisdiction: boolean,
    isBlockJurisdictionMsg: string
  ): { isBlockJurisdiction: boolean, isBlockJurisdictionMsg: string } {
    const country = this.sessionService.getCountry();
    const state = this.sessionService.getState();

    if (!country || !state)
      return {
        isBlockJurisdiction: false,
        isBlockJurisdictionMsg: ''
      };

    if (countryStateList === undefined)
      return {
        isBlockJurisdiction: false,
        isBlockJurisdictionMsg: ''
      };

    let jurisdiction: any;
    if (countryStateList[state] === undefined)
      jurisdiction = countryStateList[state];

    if (!jurisdiction)
      return {
        isBlockJurisdiction: false,
        isBlockJurisdictionMsg: ''
      };

    if (jurisdiction.WageringBlocks) {
      for (const [key, value] of Object.entries(jurisdiction.WageringBlocks)) {
        if (key.toLowerCase() === 'all') {
          isBlockJurisdiction = true;
          isBlockJurisdictionMsg = `
            ${state} Residents may not bet for ${selectedEvent?.eventName} per contract
          `;
        }
        else if (parseInt(key, 10) === eventId) {
          isBlockJurisdiction = true;
          isBlockJurisdictionMsg = value as string;
        }
        else if (!isBlockJurisdiction && key.length === 1) {
          const trackType = selectedEvent?.ToteResponse.EventDetail.TrackType;
          if (trackType === parseInt(key, 10)) {
            isBlockJurisdiction = true;
            isBlockJurisdictionMsg = value as string;
          }
        }

        if (isBlockJurisdiction) {
          break;
        }
      }
    }

    return {
      isBlockJurisdiction,
      isBlockJurisdictionMsg
    };
  }

  /**
   * Build dutch wager
   * @param {any[]} storeStructure - Store structure
   * @param {any} selectedEvent - Selected event
   * @param {any} selectedRace - Selected race
   * @param {string} selectedModifier - Selected modifier
   * @param {any} selectedPool - Selected pool
   * @param {number} baseAmount - Base amount
   * @param {any[]} poolMatrix - Pool matrix
   * @param {any[]} events - Events
   * @param {number} maxWagerTotal - Max wager total
   * @param {boolean} cantBet - Cant bet
   * @param {string} dutchROI - Dutch roi
   * @param {any} dutchTicket - Dutch ticket
   * @param {string} dutchAbcErrorMsg - Dutch abc error message
   * @param {boolean} isBlockJurisdiction - Is block jurisdiction
   * @param {string} isBlockJurisdictionMsg - Is block jurisdiction message
   * @returns {object} - Build dutch wager
   */
  buildDutchWager(
    storeStructure?: any[],
    selectedEvent?: any,
    selectedRace?: any,
    selectedModifier?: string,
    selectedPool?: any,
    baseAmount?: number,
    poolMatrix?: any[],
    events?: any[],
    maxWagerTotal?: number,
    cantBet?: boolean,
    dutchROI?: string,
    dutchTicket?: any,
    dutchAbcErrorMsg?: string,
    isBlockJurisdiction?: boolean,
    isBlockJurisdictionMsg?: string
  ): {
    dutchTotalAmount: number,
    dutchROI: string,
    dutchTicket: any,
    runnersWithStake: any,
    dutchAbcErrorMsg: string,
    disableSavedBetBtn: boolean,
    disableBetNowBtn: boolean,
    cantBet: boolean
  } {

    const dutchEnam: string[] = [
      'None',
      'DutchCost',
      'DutchPayout',
      'ABC'
    ];

    let runnersWithStake: any;

    let disableSavedBetBtn: boolean = false;
    let disableBetNowBtn: boolean = false;

    let dutchTotalAmount: number = 0;

    if (selectedModifier?.toLowerCase() === 'dutchcost') {

      if (poolMatrix?.[selectedPool?.PoolId] == undefined) {
        dutchTotalAmount = 0;
        dutchAbcErrorMsg = cantBet ?
        'Increase stake/adjust selections.'
          :
        'No valid wagers. Increase stake/adjust selections.';

        dutchROI = 'ROI: 0%';

        dutchTicket = new ticketRequest(
          '',
          '',
          '',
          [],
          '',
          ''
        );
        return {
          dutchTotalAmount,
          dutchROI,
          dutchTicket,
          runnersWithStake,
          dutchAbcErrorMsg,
          disableSavedBetBtn,
          disableBetNowBtn,
          cantBet: cantBet || false
        };
      }

      const runnersEnmList = this.transformLegsRunners(storeStructure || []);

      dutchTicket = new dutchWagersService().buildDutchWagers(
        dutchEnam.indexOf(selectedModifier),
        runnersEnmList,
        'Straight',
        selectedPool,
        baseAmount,
        poolMatrix[selectedPool?.PoolId]?.response?.ToteResponse,
        events || [],
        selectedRace,
        selectedEvent,
        maxWagerTotal || 0,
        isBlockJurisdiction || false,
        isBlockJurisdictionMsg || ''
      );

      runnersWithStake = new winDutchService().getWinDutchData(
        dutchEnam.indexOf(selectedModifier),
        runnersEnmList,
        baseAmount || 0,
        selectedEvent,
        poolMatrix[selectedPool?.PoolId]?.response?.ToteResponse
      );

      let dutchErrorFound = false;

      if (dutchTicket.Wagers) {
        for (const wager of dutchTicket.Wagers) {
          if (wager.Error !== undefined && wager.Error !== null) {
            dutchErrorFound = true;
            break;
          }
        }
      }

      if (dutchTicket.calcAmount() === 0) {
        dutchAbcErrorMsg = cantBet ?
        'Increase stake/adjust selections.'
          :
        'No valid wagers. Increase stake/adjust selections.';
      }
      else if (dutchErrorFound) {
        dutchAbcErrorMsg = 'Invalid wager exists. Increase stake/adjust selections.';
      }
      else {
        dutchAbcErrorMsg = '';
      }

      if (dutchTicket.DutchProjections.ProjectedMinROI !== 'undefined') {
        dutchROI = 'ROI: ' + dutchTicket.DutchProjections.ProjectedMinROI + '%';
      }

      if (dutchTicket.Wagers.length == 0) {
        disableSavedBetBtn = true;
        disableBetNowBtn = true;
      }
      else {
        disableSavedBetBtn = false;
        disableBetNowBtn = false;
      }

      if (dutchTicket.calcAmount() < 1)
        dutchTotalAmount = 0;

      dutchTotalAmount = dutchTicket.calcAmount();
    }

    return {
      dutchTotalAmount,
      dutchROI: dutchROI || '',
      dutchTicket,
      runnersWithStake,
      dutchAbcErrorMsg: dutchAbcErrorMsg || '',
      disableSavedBetBtn,
      disableBetNowBtn,
      cantBet: cantBet || false
    }
  }

  /**
   * Build abc wager
   * @param {any[]} storeStructure - Store structure
   * @param {any} selectedEvent - Selected event
   * @param {any} selectedRace - Selected race
   * @param {string} selectedModifier - Selected modifier
   * @param {any} selectedPool - Selected pool
   * @param {number} baseAmount - Base amount
   * @param {number} maxWagerTotal - Max wager total
   * @param {boolean} cantBet - Cant bet
   * @param {any} abcTicket - ABC ticket
   * @param {string} dutchAbcErrorMsg - Dutch abc error message
   * @param {boolean} isBlockJurisdiction - Is block jurisdiction
   * @param {string} isBlockJurisdictionMsg - Is block jurisdiction message
   * @param {ElementRef} elementRef - Element reference
   * @returns {object} - Build abc wager
   */
  buildAbcWager(
    storeStructure: any[],
    selectedEvent: any,
    selectedRace: any,
    selectedModifier: string,
    selectedPool: any,
    baseAmount: number,
    maxWagerTotal: number,
    cantBet: boolean,
    abcTicket: any,
    dutchAbcErrorMsg: string,
    isBlockJurisdiction: boolean,
    isBlockJurisdictionMsg: string,
    elementRef: ElementRef,
  ): {
    abcTotalAmount: number,
    abcTicket: any,
    dutchAbcErrorMsg: string,
    disableSavedBetBtn: boolean,
    disableBetNowBtn: boolean,
    cantBet: boolean
  } {

    let disableSavedBetBtn: boolean = false;
    let disableBetNowBtn: boolean = false;

    let abcTotalAmount: number = 0;

    if (selectedModifier.toLowerCase() === 'abc') {

      const runnersEnm = this.transformLegsRunners(storeStructure);
      const legsArray = _.map(runnersEnm, (runnerEnm) => new leg(false, runnerEnm));

      const runnersEnmList = _.map(runnersEnm, (nestedArray, i) =>
        _.map(nestedArray, (runnerId, z) => {
          const selector = `.table-runner .runner-${runnerId}.leg-${i + 1}`;
          const element = elementRef.nativeElement.querySelector(selector);
          return (element?.innerText+'%'+runnerId) || '';
        })
      );

      abcTicket = new abcWagersService().buildABCWagers(
        runnersEnmList,
        selectedPool,
        baseAmount,
        runnersEnmList,
        legsArray,
        selectedEvent,
        selectedRace,
        selectedModifier,
        maxWagerTotal,
        isBlockJurisdiction,
        isBlockJurisdictionMsg
      );

      let abcErrorFound = false;
      let allTicketsvalid =  true;
      for(let i = 0; i < abcTicket.length; i++) {
        if (abcTicket[i].ABCTicket.Wagers != null) {
          for(
            let indexOfABCWager = 0;
            indexOfABCWager < abcTicket[i].ABCTicket.Wagers.length;
            indexOfABCWager++
          ) {
            if(abcTicket[i].ABCTicket.Wagers[indexOfABCWager].Error != null) {
              if(!abcErrorFound) abcErrorFound = true;
              if(
                abcTicket[i].ABCTicket.Wagers[indexOfABCWager].Error == 'No valid wager possible'
              ) {
                allTicketsvalid = false;
                break;
              }
            }
          }
        }
      }

      if(!allTicketsvalid){
        dutchAbcErrorMsg = 'Some invalid combinations exist. Increase stake or adjust selections.';
      }
      else if (
        abcTicket != null &&
        abcTicket != -1 &&
        abcTicket[0] != null &&
        abcTicket[0].ABCTicket.calcAmount() == 0
      ) {
        dutchAbcErrorMsg = cantBet ?
        'Increase stake/adjust selections.'
          :
        'No valid wagers. Increase stake/adjust selections.';
      }
      else if(abcErrorFound) {
        dutchAbcErrorMsg = cantBet ?
        'Increase stake/adjust selections.'
          :
        'No valid wagers. Increase stake/adjust selections.';
      }
      else {
        dutchAbcErrorMsg = '';
      }

      for(let i = 0; i < abcTicket.length; i++) {
        if(abcTicket[i].ABCTicket != null)
          abcTotalAmount += parseFloat(abcTicket[i].ABCTicket.calcAmount());
      }

      if(
        abcTicket != null &&
        abcTicket != -1 &&
        abcTicket[0] != null &&
        abcTicket[0].ABCTicket != null &&
        abcTicket[0].ABCTicket.Wagers[0] != null
      ) {
        // .wagerReces abcTicket[0].ABCTicket.Wagers[0].getwagerString()[2];
        // .cond abcTicket[0].ABCTicket.Wagers[0].ConditionMTPToString();
      }

      if (
        abcTicket != null &&
        abcTicket != -1 &&
        abcTicket[0] != null &&
        abcTicket[0].ABCTicket != null &&
        abcTicket[0].ABCTicket.Wagers.length > 0
      ) {
        disableSavedBetBtn = true;
        disableBetNowBtn = true;
      }
      else {
        disableSavedBetBtn = false;
        disableBetNowBtn = false;
      }
    }

    return {
      abcTotalAmount,
      abcTicket,
      dutchAbcErrorMsg,
      disableSavedBetBtn,
      disableBetNowBtn,
      cantBet: cantBet || false
    }
  }

}
